<script lang="ts">
  import { Button } from '@athena-lib/Button';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { TextField } from '@athena-lib/Fields';
  import { HeadingSmall, Text } from '@athena-lib/Text';
  import type { LoginFormHandler } from '@athena-lib/LoginForm';
  import { onMount } from 'svelte';
  import { handler, required } from './utils';
  import Notifications from './Notifications.svelte';
  import { type LoginFormTrackingHandler } from './types';

  type Props = {
    onResetPassword: LoginFormHandler;
    goToLogin: () => void;
    onFormTracking: LoginFormTrackingHandler;
  };

  let { onResetPassword, goToLogin, onFormTracking }: Props = $props();

  let email = $state('');
  let errors = $state<string[] | undefined>(undefined);
  let messages = $state<string[] | undefined>(undefined);

  let loginFormElement: HTMLFormElement | undefined = undefined;

  const translations = getCommonTranslations();

  // TODO: Make sure that we are not treating success/errors differently
  // so that it isn't obvious if the account exists or not.
  const handleResetPassword = handler(onResetPassword, {
    defaultMessage: translations.generalError,
    onSuccess: (n) => {
      onFormTracking('submit', 'reset_pw', true);
      messages = n;
    },
    onError: (e) => {
      onFormTracking('error', 'reset_pw', true);
      errors = e;
    },
    reset: () => {
      errors = undefined;
      messages = undefined;
    },
  });

  onMount(() => {
    onFormTracking('show', 'reset_pw', false);
  });

  const maybeTriggerCompleteEvent = () => {
    if (!loginFormElement?.checkValidity()) {
      return;
    }

    onFormTracking('complete', 'reset_pw', false);
  };
</script>

<div class="flex flex-col">
  <div class="text-center">
    <HeadingSmall size="small">{translations.resetPassword}</HeadingSmall>
    <Text element="span">{translations.resetPasswordDescription}</Text>
  </div>
  {#if errors || messages}
    <Notifications {errors} {messages} />
  {/if}
  <form
    bind:this={loginFormElement}
    onsubmit={handleResetPassword}
    class="mt-8 flex flex-col gap-4 data-[has-notifications=true]:mt-0"
    data-has-notifications={!!errors || !!messages}
  >
    <TextField
      type="email"
      name="email"
      label={required(translations.email)}
      bind:value={email}
      required
      oninput={() => {
        onFormTracking('start', 'reset_pw', false);
        maybeTriggerCompleteEvent();
      }}
    />
    <div class="flex justify-center">
      <Button type="submit" class="max-w-fit" disabled={!email.length} wide>
        {translations.resetPassword}
      </Button>
    </div>
  </form>
  <button type="button" onclick={goToLogin} class="mt-8">
    <Text element="span" class="underline">{translations.backToSignIn}</Text>
  </button>
</div>
